import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { scaleFromWeekly } from "@/lib/rates";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function HostListingDialog({ car, onClose, onSave }) {
  const { t } = useTranslation();
  const [pickups, setPickups] = useState([]);
  const [presets, setPresets] = useState({});
  const initRates = car.rates || {};
  const initTiers = initRates.tiers || {};
  const [rateCat, setRateCat] = useState(initRates.category || car.category || "Economy");
  const [tiers, setTiers] = useState({
    2: initTiers[2] || initTiers["2"] || "", 3: initTiers[3] || initTiers["3"] || "",
    4: initTiers[4] || initTiers["4"] || "", 5: initTiers[5] || initTiers["5"] || "",
    6: initTiers[6] || initTiers["6"] || "", 7: initTiers[7] || initTiers["7"] || "",
  });
  const [extraDay, setExtraDay] = useState(initRates.extra_day || "");
  const [weekly, setWeekly] = useState(initTiers[7] || initTiers["7"] || "");
  useEffect(() => {
    api.get("/pickup-locations").then((r) => setPickups(r.data.locations || [])).catch(() => {});
    api.get("/config").then((r) => setPresets(r.data.category_rates || {})).catch(() => {});
  }, []);
  const applyPreset = (cat) => {
    setRateCat(cat);
    const p = presets[cat];
    if (p) { setTiers({ ...p.tiers }); setExtraDay(p.extra_day); setWeekly(p.tiers["7"] ?? p.tiers[7]); }
  };
  // Set a weekly price → auto-calculate all daily/tier rates (host can then tweak any).
  const applyWeekly = (val) => {
    setWeekly(val);
    const s = scaleFromWeekly(presets[rateCat], val);
    if (s) { setTiers(s.tiers); setExtraDay(s.extra_day); }
  };
  const FIELDS = [
    ["name", t("listingDialog.listingTitle"), "text"],
    ["deposit", t("listingDialog.deposit"), "number"],
    ["seats", t("listingDialog.seats"), "number"],
  ];
  const [f, setF] = useState({
    name: car.name || "",
    deposit: car.deposit ?? (String(car.category || "").toLowerCase() === "luxury" ? 300 : 200),
    seats: car.seats || 5,
    transmission: car.transmission || "Automatic",
    fuel: car.fuel || "Gasoline",
    image_url: car.image_url || "",
    description: car.description || "",
    features: (car.features || []).join(", "),
    pickup_location: car.pickup_location || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const tiersValid = [2, 3, 4, 5, 6, 7].every((d) => Number(tiers[d]) > 0) && Number(extraDay) > 0;
  const invalid = !f.name.trim() || Number(f.seats) < 1 || !tiersValid;

  const submit = async () => {
    setSaving(true);
    const payload = {
      name: f.name,
      weekly_rate: Number(tiers[7]),
      deposit: Number(f.deposit),
      seats: Number(f.seats),
      transmission: f.transmission,
      fuel: f.fuel,
      image_url: f.image_url,
      description: f.description,
      features: f.features.split(",").map((x) => x.trim()).filter(Boolean),
      pickup_location: f.pickup_location,
      rate_schedule: {
        category: rateCat,
        tiers: Object.fromEntries([2, 3, 4, 5, 6, 7].map((d) => [String(d), Number(tiers[d])])),
        extra_day: Number(extraDay),
        daily_start: presets[rateCat]?.daily_start || Math.round(Number(tiers[7]) / 7),
      },
    };
    await onSave(car.id, payload, onClose);
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="listing-dialog" className="sm:max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{t("listingDialog.editListing")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{t("listingDialog.reviewWarning")}</p>
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(([k, label, type]) => (
              <div key={k} className={k === "name" ? "col-span-2" : ""}>
                <label className="text-sm font-medium text-slate-700">{label}</label>
                <input type={type} value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`listing-${k}`} className="belo-input w-full mt-1.5" />
              </div>
            ))}
            <div>
              <label className="text-sm font-medium text-slate-700">{t("listingDialog.transmission")}</label>
              <select value={f.transmission} onChange={(e) => set("transmission", e.target.value)} data-testid="listing-transmission" className="belo-input w-full mt-1.5">
                <option>Automatic</option><option>Manual</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">{t("listingDialog.fuel")}</label>
              <select value={f.fuel} onChange={(e) => set("fuel", e.target.value)} data-testid="listing-fuel" className="belo-input w-full mt-1.5">
                <option>Gasoline</option><option>Hybrid</option><option>Electric</option><option>Diesel</option>
              </select>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <label className="text-sm font-semibold text-slate-800">{t("listingDialog.rentalRates")}</label>
              <select value={rateCat} onChange={(e) => applyPreset(e.target.value)} data-testid="listing-rate-category" className="belo-input py-2 text-sm">
                {["Economy", "SUV", "Minivan", "Cargo Van", "Pickup Truck", "Luxury"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <p className="text-xs text-slate-400">{t("listingDialog.ratesHint")}</p>
            <div className="flex items-center gap-3 rounded-lg bg-blue-50/70 border border-blue-100 p-2.5">
              <label className="text-sm font-semibold text-slate-700 whitespace-nowrap">{t("listingDialog.weeklyPrice")}</label>
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <input type="number" value={weekly} onChange={(e) => applyWeekly(e.target.value)} data-testid="listing-weekly-price" className="belo-input w-full py-2 pl-6" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2.5">
              {[2, 3, 4, 5, 6, 7].map((d) => (
                <div key={d}>
                  <label className="text-xs text-slate-500">{d === 7 ? t("listingDialog.oneWeek") : `${d} ${t("carDetail.days")}`}</label>
                  <input type="number" value={tiers[d]} onChange={(e) => { const v = e.target.value; setTiers((p) => ({ ...p, [d]: v })); if (d === 7) setWeekly(v); }} data-testid={`listing-tier-${d}`} className="belo-input w-full mt-1 py-2" />
                </div>
              ))}
              <div>
                <label className="text-xs text-slate-500">{t("listingDialog.extraDay")}</label>
                <input type="number" value={extraDay} onChange={(e) => setExtraDay(e.target.value)} data-testid="listing-extra-day" className="belo-input w-full mt-1 py-2" />
              </div>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">{t("listingDialog.photoUrl")}</label>
            <input value={f.image_url} onChange={(e) => set("image_url", e.target.value)} data-testid="listing-image_url" className="belo-input w-full mt-1.5" placeholder="https://…" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">{t("listingDialog.description")}</label>
            <textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="listing-description" className="belo-input w-full mt-1.5 resize-none" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">{t("listingDialog.features")} <span className="text-slate-400 font-normal">{t("listingDialog.featuresHint")}</span></label>
            <input value={f.features} onChange={(e) => set("features", e.target.value)} data-testid="listing-features" className="belo-input w-full mt-1.5" placeholder={t("listingDialog.featuresPlaceholder")} />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">{t("listingDialog.pickupLocation")}</label>
            <select value={f.pickup_location} onChange={(e) => set("pickup_location", e.target.value)} data-testid="listing-pickup" className="belo-input w-full mt-1.5">
              <option value="">{t("listingDialog.pickupPlaceholder")}</option>
              {pickups.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="rounded-full bg-white border border-slate-200 text-slate-700 px-5 py-2.5 font-semibold text-sm hover:bg-slate-50">{t("listingDialog.cancel")}</button>
            <button onClick={submit} disabled={saving || invalid} data-testid="listing-save-button" className="rounded-full bg-blue-600 text-white px-6 py-2.5 font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">{t("listingDialog.saveChanges")}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
